import React, { useState, useEffect } from 'react';
import { Button, Table, Glyphicon, Tooltip, Well } from 'react-bootstrap';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import { LinkContainer } from "react-router-bootstrap";
import { getBuilderTimelapses, getCapById, getUserById, getMonitors } from '../utils/bedrockAPI';
import { getUser } from '../utils/Common';
import styled from 'styled-components';
import { Player, BigPlayButton, ControlBar, VolumeMenuButton } from "video-react";
// import "video-react/dist/video-react.css";
import './TimelapseList.css';

// Statuses:
// INITIATED
// STARTED
// DOWNLOADING
// PROCESSING
// WATERMARKING
// UPLOADING
// ERROR
// COMPLETED
const StatusBadge = styled.span`
  padding: 5px 10px;
  border-radius: 15px;
  font-size: 12px;
  font-weight: 500;
  text-transform: uppercase;
  
  &.initiated {
    background-color: #e3f2fd;
    color: #1976d2;
  }
  
  &.processing {
    background-color: #fff3e0;
    color: #f57c00;
  }
  
  &.completed {
    background-color: #e8f5e9;
    color: #2e7d32;
  }
  
  &.failed {
    background-color: #ffebee;
    color: #c62828;
  }

  &.downloading {
    background-color: #e3f2fd;
    color: #1976d2;
  }

  &.uploading {
    background-color: #e3f2fd;
    color: #1976d2;
  }
`;

const CopyTooltip = ({ id, children }) => (
  <Tooltip id={id}>
    Click to copy link
  </Tooltip>
);

const VideoWell = styled(Well)`
  margin: 0;
  border-radius: 0;
  background-color: #f8f9fa;
  border-top: 1px solid #ddd;
  border-bottom: 1px solid #ddd;
  padding: 20px;
`;

const VideoContainer = styled.div`
  width: 100%;
  margin: 0 auto;
  max-width: 100%;
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-bottom: 10px;

  .btn {
    font-family: inherit;
    font-size: 14px;
    font-weight: 500;
  }

  .glyphicon {
    font-size: 14px;
    margin-right: 5px;
  }
`;

export default function TimelapseList(props) {
  const [timelapses, setTimelapses] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isMobile, setIsMobile] = useState(false);
  const [caps, setCaps] = useState({});
  const [showCopiedTooltip, setShowCopiedTooltip] = useState(false);
  const [users, setUsers] = useState({});
  const [isMounted, setIsMounted] = useState(true);
  const [expandedTimelapseId, setExpandedTimelapseId] = useState(null);
  const [copySuccess, setCopySuccess] = useState(false);

  useEffect(() => {
    loadTimelapses();
    const checkMobile = () => {
      if(isMounted) {
        setIsMobile(window.innerWidth <= 768);
      }
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => {
      window.removeEventListener('resize', checkMobile);
      setIsMounted(false);
    };
  }, [props.match.params.builderid]);

  const loadTimelapses = async () => {
    try {
      const timelapseList = await getBuilderTimelapses(props.match.params.builderid);
      timelapseList.sort((a,b) => new Date(b.addDate) - new Date(a.addDate));
      setTimelapses(timelapseList);
      
      // Load cap information for each timelapse
      const capPromises = timelapseList.map(timelapse => getCapById(timelapse.capId));
      const capResults = await Promise.all(capPromises);
      const capsMap = {};
      capResults.forEach(async cap => {
        if (cap) {
          capsMap[cap.capid] = cap;
          capsMap[cap.capid].monitors = await getMonitors(cap.capid);
        }
      });
      setCaps(capsMap);
    //   console.log(capsMap);

      const userPromises = timelapseList.map(timelapse => getUserById(timelapse.requestedBy));
      const userResults = await Promise.all(userPromises);
      const usersMap = {};
      userResults.forEach(user => {
        if (user) {
          usersMap[user.userId] = user;
        }
      });
      setUsers(usersMap);

      setIsLoading(false);
    } catch (error) {
      console.error('Error loading timelapses:', error);
      setIsLoading(false);
    }
  };

  const getMonitorName = (cap, monitorId) => {
    if (!cap || !cap.architecture) return 'Unknown';
    
    const monitorTypes = {
      'house': 1,
      'neighbor': 2,
      'street': 3,
      'driveway': 4
    };
    
    // Find the monitor type based on the monitorId
    for(const direction of Object.keys(cap.monitors || {})) {
      for(const monitor of Object.values(cap.monitors[direction])) {
        if (monitor.monitorId === monitorId) {
          return monitorTypes[direction] || 'Unknown';
        }
      }
    }
    
    return 'Unknown';
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatTime = (hour) => {
    return new Date().setHours(hour, 0, 0, 0).toLocaleString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const handleRowClick = (timelapse) => {
    if (timelapse.status === 'COMPLETED' && timelapse.url) {
      setExpandedTimelapseId(expandedTimelapseId === timelapse.timelapseId ? null : timelapse.timelapseId);
    }
  };

  const translateS3Url = (url) => {
    const key = url.split('bedrockwireless/')[1];
    console.log(key);
    return `https://bedrockwireless.s3.us-east-1.amazonaws.com/${key}`;
  }

  const handleCopySuccess = () => {
    setCopySuccess(true);
    setTimeout(() => setCopySuccess(false), 2000);
  };

  const translateTimelapseStatus = (status) => {
    switch(status) {
      case 'INITIATED':
        return 'initiated';
      case 'STARTED':
        return 'initiated';
      case 'COMPLETED':
        return 'completed';
      case 'ERROR':
        return 'failed';
      case 'DOWNLOADING':
        return 'downloading';
      case 'PROCESSING':
        return 'processing';
      case 'WATERMARKING':
        return 'processing';
      case 'UPLOADING':
        return 'uploading';
      default:
        return 'unknown';
    }
  }

  if (isLoading) {
    return <div>Loading...</div>;
  }

  return (
    <div className="timelapse-list">
      <div className="header">
        <h1>Timelapses</h1>
        <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', justifyContent: 'space-between', gap: '10px' }}>
          {getUser().role === 'ADMIN' && (
            <LinkContainer to={`/cameramanagement/${props.match.params.builderid}`}>
              <Button bsStyle="default" bsSize={isMobile ? 'small' : 'large'}>
                {isMobile ? 'Back' : 'Back to Camera Management'}
              </Button>
            </LinkContainer>
          )}
        </div>
      </div>

      <Table striped hover>
        <thead>
          <tr>
            <th>Timelapse Name</th>
            <th>Camera</th>
            <th>View</th>
            <th>Date Range</th>
            <th>Status</th>
            <th>Requester</th>
          </tr>
        </thead>
        <tbody>
          {timelapses.map(timelapse => (
            <React.Fragment key={timelapse.timelapseId}>
              <tr onClick={() => handleRowClick(timelapse)} key={timelapse.timelapseId}>
                <td>{timelapse.name}</td>
                <td>
                  <LinkContainer to={`/caps/${timelapse.capId}`}>
                    <Button bsSize="small" block>
                      {caps[timelapse.capId]?.name || 'Unknown'}
                    </Button>
                  </LinkContainer>
                </td>
                <td>{getMonitorName(caps[timelapse.capId], timelapse.monitorId)}</td>
                <td>
                  {formatDate(timelapse.startDate)} - {formatDate(timelapse.endDate)}
                </td>
                <td>
                  <StatusBadge className={translateTimelapseStatus(timelapse.status)}>
                    {translateTimelapseStatus(timelapse.status).toUpperCase()}
                  </StatusBadge>
                </td>
                <td>{users[timelapse.requestedBy]?.firstName || 'Unknown'} {users[timelapse.requestedBy]?.lastName[0]+'.' || ''}</td>
              </tr>
              {expandedTimelapseId === timelapse.timelapseId && timelapse.status === 'COMPLETED' && timelapse.url && (
                <tr>
                  <td colSpan="7" style={{ padding: 0 }}>
                    <VideoWell>
                      <VideoContainer>
                        <ButtonContainer>
                          <CopyToClipboard text={translateS3Url(timelapse.url)} onCopy={handleCopySuccess}>
                            <Button 
                              bsSize="small" 
                              title={copySuccess ? "Copied!" : "Share Timelapse Link"}
                            >
                              <span className={`glyphicon ${copySuccess ? 'glyphicon-ok' : 'glyphicon-copy'}`} /> Copy Timelapse Link
                            </Button>
                          </CopyToClipboard>
                        </ButtonContainer>
                        <Player>
                          <source src={translateS3Url(timelapse.url)} />
                          <BigPlayButton position="center" />
                          <ControlBar>
                            <VolumeMenuButton disabled />
                          </ControlBar>
                        </Player>
                      </VideoContainer>
                    </VideoWell>
                  </td>
                </tr>
              )}
            </React.Fragment>
          ))}
        </tbody>
      </Table>
    </div>
  );
} 